/**
 * /my-coaching/business/lead-magnets/leads/{id} — one lead: each attempt with its
 * wheel, lowest and highest areas and results-email state; the follow-up
 * card (status, first reply, follow-up emails); the latest consent record;
 * account state; and Delete lead. Another coach's lead is
 * not found.
 *
 * Aliases: coach lead page, lead detail, delete lead, lead follow-up
 */

import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardTitle } from "~/components/ui/card";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { PageSection } from "~/components/ui/page-section";
import { Wheel } from "~/components/wheel/wheel";
import { deleteMyCoachLeadFn } from "~/lib/functions/coach-leads";
import {
  myCoachLeadQueryOptions,
  myCoachLeadsQueryKey,
} from "~/lib/queries/coachLeadsQuery";
import {
  highestAreas,
  lowestAreas,
  type CoachLeadAttempt,
  type CoachLeadEmailState,
  type ScoredLabel,
} from "~/lib/shared/coach-leads";
import type { LeadSource } from "~/lib/shared/lead-source";

import { LeadFollowUpCard } from "../../-components/lead-follow-up-card";
import { formatLeadTime } from "../../-components/lead-time";

export const Route = createFileRoute(
  "/_user/my-coaching/business/lead-magnets/leads/$leadId",
)({
  staticData: { title: "Lead" },
  loader: async ({ context, params }) => {
    const lead = await context.queryClient.ensureQueryData(
      myCoachLeadQueryOptions(params.leadId),
    );
    if (!lead) throw notFound();
    return { title: lead.firstName };
  },
  component: LeadPage,
});

const EMAIL_STATE_LABELS: Record<CoachLeadEmailState, string> = {
  sent: "sent",
  sending: "sending",
  not_delivered: "not delivered",
};

function CameFromLine({ source }: { source: LeadSource }): React.JSX.Element {
  const detail =
    source.kind === "utm" && source.detail
      ? `campaign ${source.detail}`
      : source.kind === "direct"
        ? null
        : source.detail;
  return (
    <span className="mt-1 block break-all">
      Came from{" "}
      <span className="font-medium text-gray-900">
        {source.kind === "direct" ? "Direct visit" : source.label}
      </span>
      {detail && ` · ${detail}`}
    </span>
  );
}

function areaList(areas: ScoredLabel[]): string {
  return areas.map((area) => `${area.label} ${area.score}`).join(", ");
}

function LeadPage(): React.JSX.Element {
  const { leadId } = Route.useParams();
  const { data: lead } = useSuspenseQuery(myCoachLeadQueryOptions(leadId));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => deleteMyCoachLeadFn({ data: { leadId } }),
    onSuccess: async () => {
      setConfirming(false);
      toast.success("Lead deleted");
      await navigate({ to: "/my-coaching/business/lead-magnets/leads" });
      await queryClient.invalidateQueries({ queryKey: myCoachLeadsQueryKey });
    },
    onError: () => {
      toast.error("We couldn't delete this lead. Please try again.");
    },
  });

  if (!lead) throw notFound();
  const latest = lead.attempts[0];

  return (
    <div className="space-y-8">
      <PageSection
        title={lead.firstName}
        description={
          <>
            <span className="block break-all">
              {lead.email} · first shared {formatLeadTime(lead.firstSharedAt)}
            </span>
            <CameFromLine source={lead.cameFrom} />
          </>
        }
        action={
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => setConfirming(true)}
          >
            Delete lead
          </Button>
        }
      >
        {lead.attempts.map((attempt) => (
          <AttemptCard key={attempt.id} attempt={attempt} />
        ))}

        <LeadFollowUpCard lead={lead} />

        <div className="grid gap-4 sm:grid-cols-2">
          {latest && (
            <Card>
              <CardTitle>Consent</CardTitle>
              <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-2 text-sm">
                <dt className="text-text-tertiary">Shared with you</dt>
                <dd className="text-gray-900">
                  {formatLeadTime(latest.consent.at)}
                </dd>
                <dt className="text-text-tertiary">Text shown</dt>
                <dd className="text-gray-900">
                  Version {latest.consent.version} (“Share my name, email and
                  Wheel of Life results…”)
                </dd>
                <dt className="text-text-tertiary">Came from</dt>
                <dd className="break-all text-gray-900">
                  {[
                    latest.consent.referrer,
                    ...Object.entries(latest.consent.utm ?? {}).map(
                      ([key, value]) => `${key}=${value}`,
                    ),
                  ]
                    .filter(Boolean)
                    .join(" · ") || "Direct visit"}
                </dd>
              </dl>
            </Card>
          )}
          <Card>
            <CardTitle>Account</CardTitle>
            <p className="text-text-secondary text-sm">
              {lead.joined
                ? `Joined DCM. ${lead.firstName} is in your Contacts.`
                : `Not yet. ${lead.firstName} becomes a contact when they save their results.`}
            </p>
          </Card>
        </div>
      </PageSection>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete this lead?"
        description={`This removes ${lead.firstName} and their Wheel of Life history from your leads. It cannot be undone.`}
        confirmLabel="Delete lead"
        loadingLabel="Deleting…"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}

function AttemptCard({
  attempt,
}: {
  attempt: CoachLeadAttempt;
}): React.JSX.Element {
  const when = attempt.completedAt ?? attempt.startedAt;
  return (
    <Card className="sm:flex-row sm:items-center">
      {attempt.wheel && (
        <Wheel
          mode="static"
          labels={attempt.wheel.labels}
          scores={attempt.wheel.scores}
          size={160}
          className="shrink-0 self-center"
        />
      )}
      <div className="flex min-w-0 flex-col gap-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>Wheel of Life · {formatLeadTime(when)}</CardTitle>
          {attempt.completedAt ? (
            <Badge variant="success" size="sm" radius="full">
              Completed
            </Badge>
          ) : (
            <Badge variant="pending" size="sm" radius="full">
              Started
            </Badge>
          )}
        </div>
        {attempt.wheel && (
          <p className="text-text-secondary">
            Lowest:{" "}
            {areaList(
              lowestAreas(attempt.wheel.labels, attempt.wheel.scores, 3),
            )}{" "}
            · Highest:{" "}
            {areaList(
              highestAreas(attempt.wheel.labels, attempt.wheel.scores, 1),
            )}
          </p>
        )}
        {attempt.emailState && (
          <p
            className={
              attempt.emailState === "not_delivered"
                ? "text-red-700"
                : "text-text-secondary"
            }
          >
            Results email: {EMAIL_STATE_LABELS[attempt.emailState]}
          </p>
        )}
        {!attempt.completedAt && (
          <p className="text-text-secondary">
            Did not finish. No email was sent.
          </p>
        )}
      </div>
    </Card>
  );
}
